import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from './LanguageToggle';

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(location.pathname !== '/');
  const [menuOpen, setMenuOpen] = useState(false);

  const NAV_LINKS = [
    { label: t('nav.services'), href: '#services' },
    { label: t('nav.howToOperate'), href: '#operate' },
    { label: t('nav.regulations'), href: '/regulations' },
    { label: t('nav.commissary'), href: '/commissary' },
    { label: t('nav.about'), href: '/about' },
    { label: t('nav.contact'), href: '#contact' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60 || location.pathname !== '/');
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const handleNav = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    setMenuOpen(false);
    if (href.startsWith('#')) {
      if (location.pathname === '/') {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/' + href);
      }
    } else {
      navigate(href);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20">
        {/* Logo */}
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
          className="flex items-center gap-3 cursor-pointer"
        >
          <div className="w-14 h-14 flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/aa2be49284c025d1769847343deb2d78/1ae291200abc8aebd605c69261db31da.png"
              alt="Food Truck Agency Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <span
            className={`hidden sm:block font-semibold text-base leading-tight transition-colors duration-300 ${
              scrolled ? 'text-gray-800' : 'text-white'
            }`}
          >
            My Food Truck<br />
            <span className="text-teal-500 font-bold">Agency</span>
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNav(e, link.href)}
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-600 hover:text-teal-500' : 'text-white/90 hover:text-white'
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Right actions */}
        <div className="hidden md:flex items-center gap-3">
          <LanguageToggle scrolled={scrolled} />
          <a
            href="#consultation"
            onClick={(e) => handleNav(e, '#consultation')}
            className="flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            {t('nav.bookSession')}
            <i className="ri-arrow-right-line" />
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${
            scrolled ? 'text-gray-800' : 'text-white'
          }`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNav(e, link.href)}
              className="text-gray-700 text-sm font-medium text-left cursor-pointer hover:text-teal-500 transition-colors whitespace-nowrap"
            >
              {link.label}
            </a>
          ))}
          <div className="flex items-center gap-3">
            <LanguageToggle scrolled />
            <a
              href="#consultation"
              onClick={(e) => handleNav(e, '#consultation')}
              className="flex-1 bg-teal-500 text-white text-sm font-semibold px-5 py-2.5 rounded-full cursor-pointer whitespace-nowrap text-center"
            >
              {t('nav.bookSession')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}